"use client"

import { startTransition, useOptimistic } from "react"

import {
    type SearchFilter,
    type Values,
    type ValuesFilterMeta
} from "@/lib/depict-client/types/api/ProductListingRequestV3"
import { FOCUS_VISIBLE_BORDER_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"

type RadioOptionProps = {
    item: Values
    field: string
    op: SearchFilter["op"]
    isSelected: boolean
    valueIndex: number
    filter: SearchFilter
    onAddFilter: (field: string, op: SearchFilter["op"], value: string) => void
}

export default function RadioOption({
    item,
    field,
    op,
    isSelected,
    valueIndex,
    filter,
    onAddFilter
}: RadioOptionProps) {
    const itemLabel =
        (filter.meta as ValuesFilterMeta)?.names?.[valueIndex] || String(item)
    const count = (filter.meta as ValuesFilterMeta).counts?.[valueIndex] || 0
    const isClickable = count > 0
    const [optimisticSelected, setOptimisticSelected] = useOptimistic(isSelected)

    const handleClick = () => {
        if (!isClickable) return
        startTransition(() => {
            setOptimisticSelected(!optimisticSelected)
            onAddFilter(field, op, String(item))
        })
    }

    return (
        <button
            type="button"
            data-checked={optimisticSelected}
            aria-pressed={optimisticSelected}
            disabled={!isClickable}
            onClick={handleClick}
            className={clsx(
                "group/item relative -mt-px flex h-44 w-full items-center border border-col-border-neutral pl-12 pr-12 text-left first:mt-0",
                "transition-[border-color] duration-200 ease-in-out",
                "lg:hover:z-10 lg:hover:border-col-border-subtle",
                "data-[checked=true]:bg-col-bg-calm",
                FOCUS_VISIBLE_BORDER_CLASS,
                isClickable ? "cursor-pointer" : "cursor-not-allowed opacity-50"
            )}
        >
            <span className="flex flex-1 items-center justify-between">
                <span className="pt-3 Trade_Gothic_Text_14_150_0_300">{itemLabel}</span>
                <span
                    className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-col-text-primary group-data-[checked=true]/item:bg-col-text-primary"
                    aria-hidden="true"
                >
                    <span className="h-6 w-6 rounded-full bg-white group-data-[checked=false]/item:hidden" />
                </span>
            </span>
        </button>
    )
}
